import {isAnyOf} from '@reduxjs/toolkit'

import {DateFormat, formatDateFns, formatISO, isPrevYear} from '@shared/helpers/date'

import {ErrorCode, getSettlementDetail, getSettlementHistory} from '@web/actions/saleSettlement'
import {APIStatus} from '@web/core/constants'
import {ROUTER_ORDER_SALE_SETTLEMENT} from '@web/core/routes'
import {isRejectedWithValue} from '@web/helpers/api'
import {
    BundleType,
    getProductBundleInitialState,
    ProductType,
    ReceiverTransactionDateType,
    SettlementAssignmentType,
    SubBundleType,
} from '@web/slices/productBundle/slice'
import makeSlice from '@web/slices/productBundle/sliceMaker'

import {getSettleObjectName, ProductStatusMap, ProductStatusNameMap} from '../../constants'

const productBundleSlice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(getSettlementHistory.fulfilled, (state, {payload}) => {
                payload.elements.forEach((element) => {
                    state.sequentialBundleKeys.push({
                        key: element.settleObjectNo,
                        subBundles: [{key: element.settleObjectNo, products: [element.settleObjectNo]}],
                    })

                    state.bundleMap[element.settleObjectNo] = {
                        type: BundleType.NORMAL,
                        labels: [],
                        contacts: [],
                    }
                    state.subBundleMap[element.settleObjectNo] = {
                        bundleKey: element.settleObjectNo,
                        type: SubBundleType.NORMAL,
                    }

                    const description = formatDateFns(
                        element.settleYmd,
                        isPrevYear(element.settleYmd) ? DateFormat['yyyy. MM. dd. (EEE)'] : DateFormat['MM. dd. (EEE)'],
                        {inputFormat: 'yyyyMMdd'},
                    )
                    state.productMap[element.settleObjectNo] = {
                        bundleKey: element.settleObjectNo,
                        subBundleKey: element.settleObjectNo,
                        type: ProductType.SELLING,
                        status: {
                            type: ProductStatusMap[element.settleStatusType],
                            name: ProductStatusNameMap[element.settleStatusType],
                            ...(element.settleStatusType !== 'CANCEL_SCHEDULE' && description && {description}),
                        },
                        id: element.settleObjectNo,
                        name: {text: element.productName || getSettleObjectName(element.settleObjectType) || ''},
                        ...(element.productImageUrl && {image: {src: element.productImageUrl}}),
                        price: {
                            original: element.settleAmount,
                        },
                        detailUrl: ROUTER_ORDER_SALE_SETTLEMENT.DETAIL.getHref(element.settleObjectNo),
                        naverMembership: false,
                        extra: {
                            receiver: {
                                name: element.memberNickName ?? element.memberName ?? '',
                                transactionDate: formatISO(
                                    element.settleBasisYmd ||
                                        // C2C 배포 이후 제거
                                        element.orderYmd ||
                                        '',
                                    {
                                        inputFormat: 'yyyyMMdd',
                                    },
                                ),
                                dateType: ReceiverTransactionDateType.ORDER,
                            },
                        },
                    }
                })
                state.status.bundle.type = APIStatus.RESOLVED
            })
            .addCase(getSettlementDetail.fulfilled, (state, {payload}) => {
                state.sequentialBundleKeys = [
                    {
                        key: payload.settleObjectNo,
                        subBundles: [{key: payload.settleObjectNo, products: [payload.settleObjectNo]}],
                    },
                ]
                state.bundleMap[payload.settleObjectNo] = {
                    type: BundleType.NORMAL,
                    labels: [],
                    contacts: [],
                }
                state.subBundleMap[payload.settleObjectNo] = {
                    bundleKey: payload.settleObjectNo,
                    type: SubBundleType.NORMAL,
                }

                const description = formatDateFns(
                    payload.settleYmd,
                    isPrevYear(payload.settleYmd) ? DateFormat['yyyy. MM. dd. (EEE)'] : DateFormat['MM. dd. (EEE)'],
                    {inputFormat: 'yyyyMMdd'},
                )
                state.productMap[payload.settleObjectNo] = {
                    bundleKey: payload.settleObjectNo,
                    subBundleKey: payload.settleObjectNo,
                    type: ProductType.SELLING,
                    status: {
                        type: ProductStatusMap[payload.settleStatusType],
                        name: ProductStatusNameMap[payload.settleStatusType],
                        ...(payload.settleStatusType !== 'CANCEL_SCHEDULE' && description && {description}),
                    },
                    id: payload.settleObjectNo,
                    name: {text: payload.productName || getSettleObjectName(payload.settleObjectType) || ''},
                    ...(payload.productImageUrl && {image: {src: payload.productImageUrl}}),
                    price: {
                        /**
                         * 총 정산금액이 아닌 원건/취소건에 대한 정산금액을 표시
                         * ex) 원건: 1,000원, 취소건: -1,000원
                         */
                        original: payload.settleAmount,
                    },
                    naverMembership: false,
                    extra: {
                        receiver: {
                            name: payload.memberNickName ?? payload.memberName ?? '',
                            transactionDate: formatISO(
                                payload.settleBasisYmd ||
                                    // C2C 배포 이후 제거
                                    payload.orderYmd ||
                                    '',
                                {
                                    inputFormat: 'yyyyMMdd',
                                },
                            ),
                            dateType: ReceiverTransactionDateType.ORDER,
                        },
                        title: {
                            // 구매확정 안내 tooltip
                            noticeType: 'TRANSACTION_CONFIRMED',
                        },
                    },
                }
                state.assignmentMap[payload.settleObjectNo] = [
                    {
                        type: SettlementAssignmentType.NAVIGATE_SALE_DETAIL,
                        name: '판매상세 보기',
                    },
                ]
                state.status.assignment.type = APIStatus.RESOLVED
                state.status.bundle.type = APIStatus.RESOLVED
            })
            .addMatcher(isAnyOf(getSettlementHistory.pending, getSettlementDetail.pending), () => {
                const state = getProductBundleInitialState()

                state.status.bundle.type = APIStatus.FETCHING

                return state
            })
            .addMatcher(isAnyOf(getSettlementHistory.rejected, getSettlementDetail.rejected), (state, {payload}) => {
                if (isRejectedWithValue(payload) && payload.code in ErrorCode) {
                    return
                }
                state.status.bundle.type = APIStatus.REJECTED
            })
    },
})

export default productBundleSlice.reducer
